import * as React from "react";
import { useParams } from "react-router";
import styled from "styled-components";

import { PublishedRelease } from "../../../../../../../common/types/release/published-release";
import { getTrackFullTitle } from "../../../../utils/get-track-full-title";

import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Slider from "@material-ui/core/Slider";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import Typography from "@material-ui/core/Typography";

import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

import { DetailContainer } from "../common/detail-container";
import { TrackFileInfo } from "./file-info/index";
import { createMuiTheme, PaletteType, ThemeProvider } from "@material-ui/core";
import { CollaboratorList } from "./collaborator-list";
import SimpleTrack from "../../../../../../../common/types/release/simple-track";
import { Collaborator } from "../../../../../../../services/collaborator/types/collaborator";
import Chip from "@material-ui/core/Chip";
import { AudioAsset } from "../../../../../../../services/asset/types/audio-asset";
import { getACRTrackScore } from "../../../../../../../services/release/lib/get-acr-score";
import { OwnershipInfo } from "../detail-ownership-info";

const formatScore = (asset?: AudioAsset) =>
  Math.ceil(getACRTrackScore(asset) * 100);

interface ReleaseDetailTrackProps {
  published: PublishedRelease;
  onChange: (track: SimpleTrack, collaborators: Collaborator[]) => void;
}

export const ReleaseDetailTrack: React.FC<ReleaseDetailTrackProps> = ({
  published,
  onChange,
}) => {
  const { trackId } = useParams<{ trackId: string }>();

  const track = published.tracks.find((t) => t.id === trackId);
  const asset = published.assets.find((t) => t.id === trackId);
  const collaborators = published.collaborators.filter(
    (t) => t.assetId === trackId
  );
  const owners = published.owners.filter((t) => t.assetId === trackId);

  if (!(track && asset)) {
    return null;
  }

  const theme = createMuiTheme({
    palette: {
      type: "dark" as PaletteType,
      primary: {
        main: "#999",
      },
    },
  });

  const getTrackVersion = () => {
    if (track.trackVersion) {
      return track.trackVersion;
    }

    return "";
  };

  const changeTrackAttribute = <T extends keyof SimpleTrack>(
    key: T,
    value: SimpleTrack[T]
  ) => {
    onChange({ ...track, [key]: value }, collaborators);
  };

  return (
    <ThemeProvider theme={theme}>
      <DetailContainer>
        <TrackTitle>{getTrackFullTitle(track, collaborators)}</TrackTitle>

        <TrackFileInfo asset={asset} />

        <Grid container spacing={3}>
          <Grid item xs={8}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={track.title || ""}
                  onChange={(ev) =>
                    changeTrackAttribute<"title">("title", ev.target.value)
                  }
                />
              </Grid>
              {track.isrcExists && track.ownsRights && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={track.ownsRights}
                        onChange={() => null}
                        color="default"
                      />
                    }
                    label="The artists stated they own the rights fot this track"
                  />
                </Grid>
              )}
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={track.type === "instrumental"}
                      onChange={(ev) =>
                        changeTrackAttribute<"type">(
                          "type",
                          ev.target.checked ? "instrumental" : "song"
                        )
                      }
                      color="default"
                    />
                  }
                  label="Instrumental"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Version"
                  value={getTrackVersion()}
                  onChange={(ev) =>
                    changeTrackAttribute<"trackVersion">(
                      "trackVersion",
                      ev.target.value
                    )
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="Language"
                  value={track.language || ""}
                  onChange={(ev) =>
                    changeTrackAttribute<"language">(
                      "language",
                      ev.target.value
                    )
                  }
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="Metadata"
                  value={track.metadataLanguage || ""}
                  onChange={(ev) =>
                    changeTrackAttribute<"metadataLanguage">(
                      "metadataLanguage",
                      ev.target.value
                    )
                  }
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="ISRC"
                  value={track.isrc || ""}
                  onChange={(ev) =>
                    changeTrackAttribute<"isrc">(
                      "isrc",
                      ev.target.value || undefined
                    )
                  }
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={track.isInstantGratification}
                      onChange={(ev) =>
                        changeTrackAttribute<"isInstantGratification">(
                          "isInstantGratification",
                          ev.target.checked
                        )
                      }
                      color="default"
                    />
                  }
                  label="Instant Gratification"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={track.isClean}
                      onChange={(ev) =>
                        changeTrackAttribute<"isClean">(
                          "isClean",
                          ev.target.checked
                        )
                      }
                      color="default"
                    />
                  }
                  label="Clean Version"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={track.isExplicit}
                      onChange={(ev) =>
                        changeTrackAttribute<"isExplicit">(
                          "isExplicit",
                          ev.target.checked
                        )
                      }
                      color="default"
                    />
                  }
                  label="Explicit"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" style={{ marginBottom: "2rem" }}>
                  Machine Learning Detections
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={4}>
                    {track.mlClassifierResult?.explicitness === undefined ? (
                      <Typography>Unkown</Typography>
                    ) : (
                      <Slider
                        value={track.mlClassifierResult?.explicitness || 0}
                        min={0}
                        max={1}
                        disabled
                        step={0.01}
                        valueLabelFormat={(value) => value.toFixed(2)}
                        valueLabelDisplay="on"
                      />
                    )}
                    <Typography variant="button">Explicit?</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    {track.mlClassifierResult?.vocalness === undefined ? (
                      <Typography>Unkown</Typography>
                    ) : (
                      <Slider
                        value={track.mlClassifierResult?.vocalness || 0}
                        min={0}
                        max={1}
                        disabled
                        step={0.01}
                        valueLabelFormat={(value) => value.toFixed(2)}
                        valueLabelDisplay="on"
                      />
                    )}
                    <Typography variant="button">Lyrics?</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    {track.mlClassifierResult?.popularity === undefined ? (
                      <Typography>Unkown</Typography>
                    ) : (
                      <Slider
                        value={track.mlClassifierResult?.popularity || 0}
                        min={0}
                        max={1}
                        disabled
                        step={0.01}
                        valueLabelFormat={(value) => value.toFixed(2)}
                        valueLabelDisplay="on"
                      />
                    )}
                    <Typography variant="button">Popularity</Typography>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <Typography>
                      ACR Response{" "}
                      <Chip
                        label={`${formatScore(asset)}%`}
                        color={
                          formatScore(asset) > 80 ? "secondary" : "default"
                        }
                      />
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography style={{ whiteSpace: "pre-wrap" }}>
                      {JSON.stringify(
                        asset.validation.figerprintResults || [],
                        null,
                        4
                      )}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={4}>
            <Grid item xs={12}>
              <TextField
                label="Lyrics"
                value={track.lyrics || ""}
                multiline
                fullWidth
                rowsMax={24}
              />
            </Grid>
            <Grid container style={{ marginTop: "1rem" }}>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={track.ownProduced === true}
                      onChange={(ev) =>
                        changeTrackAttribute<"ownProduced">(
                          "ownProduced",
                          ev.target.checked
                        )
                      }
                      color="default"
                    />
                  }
                  label="Produced"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={track.ownProduced === false}
                      onChange={(ev) =>
                        changeTrackAttribute<"ownProduced">(
                          "ownProduced",
                          !ev.target.checked
                        )
                      }
                      color="default"
                    />
                  }
                  label="Purchased"
                />
              </Grid>
            </Grid>
            <Grid item xs={12} style={{ marginTop: "1rem" }}>
              <Typography>Copyright Documents</Typography>
              {track.copyrightDocuments?.map((item) => (
                <a
                  style={{ color: "yellow" }}
                  target="_blank"
                  rel="noopener noreferrer"
                  href={item.url}
                >
                  {item.name}
                </a>
              ))}
            </Grid>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={6}>
            <CollaboratorList
              collaborators={collaborators.filter(
                (c) => c.type === "COLLABORATOR"
              )}
              artists={published.artists}
              title="Collaborators"
              onChange={(newCollaborators) => onChange(track, newCollaborators)}
              options={["main", "featuring"]}
            />
          </Grid>
          <Grid item xs={6}>
            <CollaboratorList
              collaborators={collaborators.filter(
                (c) => c.type === "CONTRIBUTOR"
              )}
              artists={published.artists}
              title="Credits"
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <OwnershipInfo
              title="Songwiter Ownership"
              owners={owners.filter(
                (owner) => owner.ownershipType === "WRITER"
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <OwnershipInfo
              title="Master Recording Ownership"
              owners={owners.filter((owner) => owner.ownershipType === "OWNER")}
            />
          </Grid>
        </Grid>
      </DetailContainer>
    </ThemeProvider>
  );
};

// lyrics?: string;

// previewStart?: number;

// hasMusicVideo?: boolean;
// video?: TrackVideo;

const TrackTitle = styled.h2``;
